import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { CreateVendorRequest, Guid, UpdateVendorRequest, Vendor } from "@/api/types";

export type VendorsStatus = "loading" | "ready" | "error";

export interface UseVendorsResult {
  /** `null` until resolved and on failure. */
  vendors: Vendor[] | null;
  status: VendorsStatus;
  error: string | null;
  reload: () => Promise<void>;
  /** Throws on failure — `BooksVendors.tsx` catches this itself to read per-field
   *  validation errors and the 409 name-conflict sentence off it (`problemFieldErrors`/
   *  `problemMessage`), which this hook has no opinion about. */
  create: (body: CreateVendorRequest) => Promise<Vendor>;
  update: (id: Guid, body: UpdateVendorRequest) => Promise<Vendor>;
  /** DELETE — a genuine removal, unlike `usePlaces.archive`: a vendor has no archived
   *  state (`VendorEndpoints.cs`'s own remarks). Throws on a 409 (still referenced by an
   *  entry) for the caller to surface. */
  remove: (id: Guid) => Promise<void>;
}

/**
 * ef-042-6 — the farm's own vendor list, the write half of it (`ef-042-3`'s
 * `POST`/`PATCH`/`DELETE /api/demo/vendors`; `GET` has existed since `DemoEndpoints` and
 * is reused here, never duplicated).
 *
 * Re-reads the WHOLE list after every write rather than patching the array in place —
 * same convention `usePlaces`/`useJobCodes` use, for the same reason: the list is small
 * (a farm's own vendors, not a growing transactional table) and a re-read can never
 * drift from what the server actually holds.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, not the control — same
 * reasoning `usePlaces`' own remarks give. The Vendors screen this feeds is already
 * behind the owner shell.
 */
export function useVendors(): UseVendorsResult {
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [status, setStatus] = useState<VendorsStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getVendors(signal);
      if (signal?.aborted) return;
      setVendors(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setVendors(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load vendors."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const create = useCallback(
    async (body: CreateVendorRequest): Promise<Vendor> => {
      const vendor = await demoApi.createVendor(body);
      await load();
      return vendor;
    },
    [load],
  );

  const update = useCallback(
    async (id: Guid, body: UpdateVendorRequest): Promise<Vendor> => {
      const vendor = await demoApi.updateVendor(id, body);
      await load();
      return vendor;
    },
    [load],
  );

  const remove = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.deleteVendor(id);
      await load();
    },
    [load],
  );

  return { vendors, status, error, reload, create, update, remove };
}
